<template>
  <BaseModal
    :model-value="isOpen"
    :title="store?.name ?? ''"
    max-width-class="max-w-md"
    panel-class="overflow-hidden rounded-t-2xl sm:rounded-2xl"
    :show-close-button="false"
    @close="close"
  >
    <template #content>
      <!-- Map -->
      <div class="bg-surface-level-100 dark:bg-surface-level-800 h-44 relative sm:h-48 w-full">
        <ClientOnly>
          <StoreLocationMap
            :latitude="store?.latitude ?? null"
            :longitude="store?.longitude ?? null"
            :address="addressLine ?? ''"
            :active="isOpen"
          />

          <template #fallback>
            <div
              class="bg-surface-level-100 dark:bg-surface-level-800 flex h-full items-center justify-center w-full"
            >
              <MapPinIcon
                class="dark:text-content-level-600 h-10 text-content-level-300 w-10"
                aria-hidden="true"
              />
            </div>
          </template>
        </ClientOnly>

        <button
          type="button"
          class="absolute bg-surface-canvas dark:bg-surface-level-900 dark:hover:bg-surface-level-800 dark:text-content-inverse focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none h-9 hover:bg-surface-level-50 inline-flex items-center justify-center left-3 rounded-full shadow-md text-content-level-900 top-3 transition w-9 z-(--z-index-ds-raised)"
          :aria-label="$t('common.actions.close')"
          @click="close"
        >
          <XMarkIcon class="h-5 w-5" />
        </button>
      </div>

      <!-- Content -->
      <div class="pb-6 pt-5 px-5">
        <h2 class="dark:text-content-inverse font-bold text-content-level-900 text-xl">
          {{ store?.name }}
        </h2>

        <p
          v-if="categoryLine"
          class="dark:text-content-level-400 mt-1 text-content-level-500 text-sm"
        >
          {{ categoryLine }}
        </p>

        <div class="dark:divide-border-level-700 divide-border-level-200 divide-y mt-5">
          <!-- Address -->
          <div
            v-if="addressLine"
            class="first:pt-0 flex gap-3 items-center py-4"
          >
            <MapPinIcon
              class="dark:text-content-inverse h-5 shrink-0 text-content-level-900 w-5"
              aria-hidden="true"
            />

            <p
              class="dark:text-content-inverse flex-1 font-medium leading-5 min-w-0 text-content-level-900 text-sm"
            >
              {{ addressLine }}
            </p>

            <button
              type="button"
              class="dark:hover:bg-surface-canvas/10 dark:hover:text-content-inverse dark:text-content-level-400 focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none hover:bg-surface-level-100 hover:text-content-level-700 p-1 rounded-md shrink-0 text-content-level-500 transition"
              :aria-label="
                addressCopied
                  ? $t('common.accessibility.addressCopied')
                  : $t('common.accessibility.copyAddress')
              "
              @click.stop="copyAddress"
            >
              <CheckIcon
                v-if="addressCopied"
                class="h-5 text-success-600 w-5"
              />

              <DocumentDuplicateIcon
                v-else
                class="h-5 w-5"
              />
            </button>
          </div>

          <!-- Hours -->
          <div
            v-if="todayStatus !== null"
            class="py-4"
          >
            <button
              type="button"
              class="flex focus:outline-none gap-3 items-start text-left w-full"
              :aria-expanded="hoursExpanded"
              @click="toggleHours"
            >
              <ClockIcon
                class="dark:text-content-inverse h-5 shrink-0 text-content-level-900 w-5"
                aria-hidden="true"
              />

              <div class="flex-1 min-w-0 space-y-1">
                <p
                  class="dark:text-content-inverse font-medium leading-5 text-content-level-900 text-sm"
                >
                  {{ todayStatus }}
                </p>
              </div>

              <PlusIcon
                v-if="!hoursExpanded && weeklySchedule.length > 0"
                class="dark:text-content-level-400 h-5 shrink-0 text-content-level-500 w-5"
                aria-hidden="true"
              />

              <MinusIcon
                v-else-if="hoursExpanded && weeklySchedule.length > 0"
                class="dark:text-content-level-400 h-5 shrink-0 text-content-level-500 w-5"
                aria-hidden="true"
              />
            </button>

            <div
              v-if="hoursExpanded && weeklySchedule.length > 0"
              class="ml-8 mt-3 space-y-2"
            >
              <div
                v-for="row in weeklySchedule"
                :key="row.key"
                class="flex gap-4 text-sm"
              >
                <span
                  class="shrink-0 w-24"
                  :class="
                    row.isToday
                      ? 'font-medium text-content-level-900 dark:text-content-inverse'
                      : 'text-content-level-600 dark:text-content-level-400'
                  "
                >
                  {{ row.dayLabel }}
                </span>

                <div
                  class="flex-1 min-w-0"
                  :class="
                    row.isToday
                      ? 'font-medium text-content-level-900 dark:text-content-inverse'
                      : 'text-content-level-600 dark:text-content-level-400'
                  "
                >
                  <span>{{ row.hours }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Ratings -->
          <div
            v-if="hasReviews"
            class="flex gap-3 items-start last:pb-0 py-4"
          >
            <StarIcon
              class="dark:text-content-inverse h-5 shrink-0 text-content-level-900 w-5"
              aria-hidden="true"
            />

            <div class="flex-1 min-w-0">
              <p
                class="dark:text-content-inverse font-medium leading-5 text-content-level-900 text-sm"
              >
                {{ ratingLine }}
              </p>

              <p
                v-if="store?.description"
                class="dark:text-content-level-400 mt-1 text-content-level-600 text-sm"
              >
                {{ store.description }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import {
  CheckIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  MapPinIcon,
  MinusIcon,
  PlusIcon,
  StarIcon,
  XMarkIcon
} from '@heroicons/vue/24/outline'
import type { PublicStore } from '~/types/PublicStore'

import BaseModal from '~/components/shared/dialogs/BaseModal.vue'
import StoreLocationMap from '~/components/StoreLocationMap.vue'
import { copyTextToClipboard } from '~/utils/copyTextToClipboard'
import { useStoreScheduleSummary } from '~/composables/useStoreScheduleSummary'
import { hasStoreRating } from '~/utils/hasStoreRating'
import { hasStoreReviews } from '~/utils/hasStoreReviews'

const props = withDefaults(defineProps<Props>(), {
  categories: () => []
})

const emit = defineEmits<{
  close: []
}>()

const close = () => {
  emit('close')
}
const toggleHours = () => {
  hoursExpanded.value = !hoursExpanded.value
}

interface CategoryItem {
  id?: number
  name?: string
}

interface Props {
  /** Whether the store information modal is visible. */
  isOpen: boolean
  /** Store whose information is displayed. */
  store: PublicStore | null
  /** Menu categories associated with the store. */
  categories?: CategoryItem[]
}

const hoursExpanded = ref(false)
const addressCopied = ref(false)
let addressCopiedTimer: ReturnType<typeof setTimeout> | null = null

const categoryLine = computed(() => {
  const names = props.categories
    .map((category) => category.name)
    .filter((name): name is string => typeof name === 'string')
  return names.length > 0 ? names.join(' · ') : ''
})

const addressLine = computed(() => {
  if (!props.store) return ''
  return [props.store.address, props.store.city, props.store.state, props.store.zip]
    .filter(Boolean)
    .join(', ')
})

const { todayStatus, weeklySchedule } = useStoreScheduleSummary({
  operatingHours: () => props.store?.operating_hours ?? [],
  timezone: () => props.store?.timezone
})

const hasReviews = computed<boolean>(
  () => props.store !== null && hasStoreReviews(props.store.review_count)
)

const ratingLine = computed(() => {
  if (props.store === null) return ''
  const { average_rating: rating, review_count: count } = props.store
  if (!hasStoreReviews(count)) return ''
  if (!hasStoreRating(rating)) return ''
  let countLabel = `${count} ratings`
  if (count >= 10000) {
    countLabel = '10000+ ratings'
  } else if (count === 1) {
    countLabel = '1 rating'
  }
  return `${rating} (${countLabel})`
})

async function copyAddress() {
  const address = addressLine.value
  if ((address ?? '') === '') return

  const copied = await copyTextToClipboard(address ?? '')
  if (!copied) return

  addressCopied.value = true
  if (addressCopiedTimer) clearTimeout(addressCopiedTimer)
  addressCopiedTimer = setTimeout(() => {
    addressCopied.value = false
  }, 2000)
}

watch(
  () => props.isOpen,
  (open) => {
    if (!open) {
      hoursExpanded.value = false
      addressCopied.value = false
    }
  }
)

onUnmounted(() => {
  if (addressCopiedTimer) clearTimeout(addressCopiedTimer)
})
</script>
